import type { MergedFolder } from './documentsMerge.ts';

/**
 * Moving through a client's documents one folder at a time (Arthur, 21 October, 16:12): where the reader is, as the
 * folder names from the top, so the place survives the list being fetched again (after an upload the tree is rebuilt;
 * a folder only Clio had becomes a Procura one with the same name, and the reader stays in it). A pure module, tested
 * in web/test/folder-nav.test.ts.
 */

/** Names compare as the merge compares them: capitals and spacing ignored. */
const nameKey = (name: string) => name.toLowerCase().replace(/\s+/g, '');

/**
 * The folders from the top down to `path`, as far as they still exist: an empty list is the top level. A name that is
 * no longer there ends the walk, so the reader lands on the nearest folder that is.
 */
export function trail(folders: MergedFolder[], path: readonly string[]): MergedFolder[] {
  const out: MergedFolder[] = [];
  let level = folders;
  for (const name of path) {
    const next = level.find((f) => nameKey(f.name) === nameKey(name));
    if (!next) break;
    out.push(next);
    level = next.children;
  }
  return out;
}

/** What the back button says: nothing at the top level; "Documents" inside a top folder; else the parent's name. */
export function backLabel(at: readonly MergedFolder[]): string | null {
  if (at.length === 0) return null;
  if (at.length === 1) return 'Documents';
  return at[at.length - 2]!.name;
}

/** The title in the middle of the bar: the folder the reader is in, or "Documents" at the top. */
export function titleOf(at: readonly MergedFolder[]): string {
  return at.length === 0 ? 'Documents' : at[at.length - 1]!.name;
}

/** The path of the folder one level up. */
export const up = (path: readonly string[]): string[] => path.slice(0, -1);
